<template>
  <div class="ai-settings">
    <div class="container-fluid py-4">
      <h2 class="h4 fw-medium mb-4">{{ $t("__aiSettings") || "AI 設定管理" }}</h2>

      <!-- 篩選和搜尋區 -->
      <div class="d-flex flex-wrap align-items-center justify-content-start justify-content-md-between">
        <div class="device-select me-2 mb-2">
          <select v-model="filterType" class="form-select rounded-pill me-2">
            <option :value="null">{{ $t("__all") }}</option>
            <option value="global">{{ $t("__globalSetting") }}</option>
            <option value="agency">{{ $t("__aiSettingsAgencyConfig") }}</option>
          </select>
        </div>

        <div v-if="filterType === 'agency'" class="d-flex align-items-center me-2 mb-2">
          <div class="device-report-select">
            <select v-model="searchAgency" class="form-select rounded-pill me-2">
              <option :value="null">{{ $t("__all") + " " + $t('__agency') }}</option>
              <option v-for="item in allAgencys" :value="item.id" :key="'agent'+ item.id">
                {{ item.name + ' / ' + item.codename }}
              </option>
            </select>
          </div>
        </div>

        <div class="ms-auto mb-2">
          <button
            v-if="permissions && permissions.some(permission => permission.name === 'ai.settings.create')"
            @click="showModal()"
            type="button"
            class="btn btn-primary rounded-pill"
          >
            <i class="bi bi-plus-circle me-2"></i>{{ $t("__add") || "新增設定" }}
          </button>
        </div>
      </div>

      <!-- 測試模式管理區 -->
      <div class="card mt-3 border-warning" v-if="globalTestSetting">
        <div class="card-header bg-warning bg-opacity-10">
          <h5 class="mb-0 fw-medium">
            <i class="bi bi-bug me-2"></i>{{ $t("__aiSettingsTestMode") }}
          </h5>
        </div>
        <div class="card-body">
          <div class="row align-items-center">
            <div class="col-md-3 d-flex align-items-center">
              <div class="status-switch me-3">
                <input
                  type="checkbox"
                  :id="'testModeSwitch'"
                  v-model="testMode"
                  @change="updateTestMode"
                />
                <label :for="'testModeSwitch'"></label>
              </div>
              <span class="text-muted">
                {{ testMode ? $t('__aiSettingsTestModeOn') : $t('__aiSettingsTestModeOff') }}
              </span>
            </div>
            <div class="col-md-9" v-if="testMode">
              <label class="form-label">{{ $t("__aiSettingsTestGroups") }}：</label>
              <v-select
                v-model="testGroupIds"
                :options="availableGroups"
                :reduce="group => group.group_id"
                label="display_name"
                multiple
                :placeholder="$t('__aiSettingsSelectTestGroups')"
                @input="updateTestGroups"
              >
                <template v-slot:option="option">
                  <div>
                    <strong>{{ option.display_name || $t('__aiSettingsUnnamedGroup') }}</strong>
                    <br>
                    <small class="text-muted">
                      {{ option.agency ? option.agency.name : $t('__aiSettingsNoAgency') }}
                      <span v-if="option.bind_id">- {{ option.bind_id }}</span>
                    </small>
                  </div>
                </template>
              </v-select>
              <small class="text-muted">
                <i class="bi bi-info-circle me-1"></i>
                {{ $t("__aiSettingsTestModeUpdateHint") }}
              </small>
            </div>
          </div>
          <div v-if="testMode && testGroupIds.length > 0" class="mt-3">
            <div class="alert alert-warning mb-0">
              <i class="bi bi-exclamation-triangle me-2"></i>
              <strong>{{ $t('__aiSettingsTestModeOn') }}</strong>
              {{ $t('__aiSettingsTestModeWarning', { count: testGroupIds.length }) }}
            </div>
          </div>
        </div>
      </div>

      <!-- 資料表格 -->
      <div class="card mt-3">
        <div class="card-body">
          <b-table
            id="ai-settings-table"
            :responsive="$store.state.windowWidth > $store.state.breakpoints.sm"
            :stacked="$store.state.windowWidth <= $store.state.breakpoints.sm"
            :items="filterItem"
            :fields="fields"
            :filter="filter"
            :filter-included-fields="filterOn"
            :current-page="currentPage"
            :per-page="perPage"
            :sort-by="sortBy"
            :sort-desc="sortDesc"
            @filtered="onFiltered"
            striped
            borderless
            no-provider-filtering
            head-variant="light"
            show-empty
            :empty-text="$t('__noData')"
            :empty-filtered-text="$t('__notMatched')"
          >
            <!-- 表頭樣式 -->
            <template v-slot:head()="scope">
              <div class="text-nowrap">
                {{ scope.label }}
              </div>
            </template>

            <!-- 提供商欄位 -->
            <template v-slot:cell(provider)="data">
              <span class="badge bg-info text-white">{{ data.value.toUpperCase() }}</span>
            </template>

            <!-- 機構欄位 -->
            <template v-slot:cell(agency)="data">
              {{ data.item.agency ? data.item.agency.name : $t('__globalSetting') }}
            </template>

            <!-- 模型欄位 -->
            <template v-slot:cell(model)="data">
              <div>
                <small class="text-muted">{{ $t("__aiSettingsMainModel") }}：</small>{{ data.item.model }}<br>
                <small class="text-muted">{{ $t("__aiSettingsEmbeddingModel") }}：</small>{{ data.item.embedding_model }}
              </div>
            </template>

            <!-- 優先順序欄位 -->
            <template v-slot:cell(priority)="data">
              <span class="badge bg-primary">{{ data.value || 0 }}</span>
            </template>

            <!-- 狀態欄位 -->
            <template v-slot:cell(is_active)="data">
              <span v-if="data.value" class="badge bg-success">{{ $t("__enabled") }}</span>
              <span v-else class="badge bg-secondary">{{ $t("__disabled") }}</span>
            </template>

            <!-- 統計欄位 -->
            <template v-slot:cell(statistics)="data">
              <div class="small">
                <div>{{ $t("__aiSettingsTotalRequests") }}：{{ data.item.total_requests || 0 }}</div>
                <div>{{ $t("__aiSettingsSuccessResponses") }}：{{ data.item.successful_responses || 0 }}</div>
                <div v-if="data.item.total_requests > 0" class="text-success">
                  {{ $t("__aiSettingsSuccessRate") }}：{{ ((data.item.successful_responses / data.item.total_requests) * 100).toFixed(1) }}%
                </div>
              </div>
            </template>

            <!-- 操作欄位 -->
            <template v-slot:cell(actions)="data">
              <div class="d-flex">
                <button
                  v-if="permissions && permissions.some(permission => permission.name === 'ai.settings.show')"
                  @click="showDetails(data.item)"
                  type="button"
                  class="btn btn-link px-1 text-info"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__viewDetail')"
                >
                  <i class="bi bi-eye mx-1 fs-5"></i>
                </button>
                <button
                  v-if="permissions && permissions.some(permission => permission.name === 'ai.settings.update')"
                  @click="showModal(data.item.id)"
                  type="button"
                  class="btn btn-link px-1 text-dark"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__edit')"
                >
                  <i class="bi bi-pencil mx-1 fs-5"></i>
                </button>
                <button
                  v-if="permissions && permissions.some(permission => permission.name === 'ai.settings.test')"
                  @click="testSetting(data.item.id)"
                  type="button"
                  class="btn btn-link px-1 text-warning"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__aiSettingsTestConfig')"
                >
                  <i class="bi bi-play-circle mx-1 fs-5"></i>
                </button>
                <button
                  v-if="permissions && permissions.some(permission => permission.name === 'ai.settings.create')"
                  @click="duplicateSetting(data.item.id)"
                  type="button"
                  class="btn btn-link px-1 text-primary"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__aiSettingsDuplicate')"
                >
                  <i class="bi bi-files mx-1 fs-5"></i>
                </button>
                <button
                  v-if="permissions && permissions.some(permission => permission.name === 'ai.settings.delete')"
                  @click="deleteSettingAlert(data.item.id)"
                  type="button"
                  class="btn btn-link px-1 text-danger"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__delete')"
                >
                  <i class="bi bi-trash fs-5"></i>
                </button>
              </div>
            </template>
          </b-table>
        </div>
      </div>

      <!-- 分頁 -->
      <div class="d-flex justify-content-center mt-3" v-if="totalRows > perPage">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="center"
          @change="changePage"
        ></b-pagination>
      </div>
    </div>

    <!-- 編輯 Modal -->
    <div class="modal fade cms-style" tabindex="-1" id="editAiSetting" ref="editModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <form @submit.prevent="submitForm">
            <div class="modal-header">
              <h5 class="modal-title fw-medium">
                {{ isEdit ? $t('__aiSettingsEditTitle') : $t('__aiSettingsAddTitle') }}
              </h5>
              <button
                type="button"
                class="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div class="modal-body">
              <!-- 基本設定 -->
              <div class="border rounded p-3 mb-3">
                <h6 class="fw-bold mb-3">{{ $t("__aiSettingsBasicConfig") }}</h6>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="agency_id" class="form-label fw-medium">{{ $t("__agency") }}</label>
                    <select
                      v-model="modalData.agency_id"
                      id="agency_id"
                      class="form-select"
                      :class="{ 'is-invalid': validate && validate.agency_id }"
                    >
                      <option :value="null">{{ $t("__globalSetting") }}</option>
                      <option v-for="agency in allAgencys" :key="agency.id" :value="agency.id">
                        {{ agency.name }}
                      </option>
                    </select>
                    <div v-if="validate && validate.agency_id" class="invalid-feedback">
                      {{ validate.agency_id[0] }}
                    </div>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label for="provider" class="form-label fw-medium required">{{ $t("__aiSettingsProvider") }}</label>
                    <select
                      v-model="modalData.provider"
                      id="provider"
                      class="form-select"
                      :class="{ 'is-invalid': validate && validate.provider }"
                      required
                    >
                      <option value="openai">OpenAI</option>
                      <option value="azure">Azure OpenAI</option>
                      <option value="claude">Claude</option>
                    </select>
                    <div v-if="validate && validate.provider" class="invalid-feedback">
                      {{ validate.provider[0] }}
                    </div>
                  </div>
                </div>

                <div class="mb-3">
                  <label for="api_key" class="form-label fw-medium required">
                    {{ $t("__aiSettingsApiKey") }}
                    <button
                      type="button"
                      class="btn btn-sm btn-link"
                      @click="validateApiKey"
                      v-if="modalData.api_key"
                    >
                      <i class="bi bi-check-circle"></i> {{ $t("__aiSettingsValidate") }}
                    </button>
                  </label>
                  <input
                    v-model="modalData.api_key"
                    type="text"
                    id="api_key"
                    class="form-control"
                    :class="{ 'is-invalid': validate && validate.api_key }"
                    :placeholder="isEdit ? $t('__aiSettingsKeepOriginal') : $t('__aiSettingsEnterApiKey')"
                    :required="!isEdit"
                  />
                  <div v-if="validate && validate.api_key" class="invalid-feedback">
                    {{ validate.api_key[0] }}
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="model" class="form-label fw-medium required">{{ $t("__aiSettingsMainModel") }}</label>
                    <v-select
                      v-model="modalData.model"
                      :options="availableModels.main"
                      taggable
                      :placeholder="$t('__aiSettingsSelectOrInput')"
                      class="style-chooser"
                      :class="{ 'is-invalid': validate && validate.model }"
                      id="model"
                    >
                      <template #search="{ attributes, events }">
                        <input
                          class="vs__search"
                          v-bind="attributes"
                          v-on="events"
                        />
                      </template>
                      <template #no-options>
                        <span v-if="modelsNote">💡 {{ modelsNote }}</span>
                        <span v-else>{{ $t("__aiSettingsCustomModelName") }}</span>
                      </template>
                    </v-select>
                    <small v-if="modelsNote" class="text-muted">
                      ⚠️ {{ modelsNote }}
                    </small>
                    <div v-if="validate && validate.model" class="invalid-feedback">
                      {{ validate.model[0] }}
                    </div>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label for="embedding_model" class="form-label fw-medium required">{{ $t("__aiSettingsEmbeddingModel") }}</label>
                    <v-select
                      v-model="modalData.embedding_model"
                      :options="availableModels.embedding"
                      taggable
                      :placeholder="$t('__aiSettingsSelectOrInput')"
                      class="style-chooser"
                      :class="{ 'is-invalid': validate && validate.embedding_model }"
                      id="embedding_model"
                    >
                      <template #search="{ attributes, events }">
                        <input
                          class="vs__search"
                          v-bind="attributes"
                          v-on="events"
                        />
                      </template>
                      <template #no-options>
                        <span v-if="embeddingNote">💡 {{ embeddingNote }}</span>
                        <span v-else>輸入自訂模型名稱</span>
                      </template>
                    </v-select>
                    <small v-if="embeddingNote" class="text-muted">
                      ⚠️ {{ embeddingNote }}
                    </small>
                    <div v-if="validate && validate.embedding_model" class="invalid-feedback">
                      {{ validate.embedding_model[0] }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- 觸發條件 -->
              <div class="border rounded p-3 mb-3">
                <h6 class="fw-bold mb-3">{{ $t("__aiSettingsTriggerConditions") }}</h6>

                <div class="mb-3">
                  <label class="form-label fw-medium">{{ $t("__aiSettingsTriggerKeywords") }}</label>
                  <v-select
                    v-model="modalData.trigger_keywords"
                    :options="defaultTriggerKeywords"
                    multiple
                    taggable
                    :placeholder="$t('__aiSettingsInputOrSelect')"
                  ></v-select>
                </div>

                <div class="row">
                  <div class="col-md-4 mb-3">
                    <div class="form-check">
                      <input
                        v-model="modalData.trigger_on_question"
                        type="checkbox"
                        class="form-check-input"
                        id="trigger_on_question"
                      />
                      <label class="form-check-label" for="trigger_on_question">
                        {{ $t("__aiSettingsTriggerOnQuestion") }}
                      </label>
                    </div>
                  </div>

                  <div class="col-md-4 mb-3">
                    <div class="form-check">
                      <input
                        v-model="modalData.trigger_on_mention"
                        type="checkbox"
                        class="form-check-input"
                        id="trigger_on_mention"
                      />
                      <label class="form-check-label" for="trigger_on_mention">
                        {{ $t("__aiSettingsTriggerOnMention") }}
                      </label>
                    </div>
                  </div>

                  <div class="col-md-4 mb-3">
                    <label for="trigger_prefix" class="form-label fw-medium">{{ $t("__aiSettingsTriggerPrefix") }}</label>
                    <input
                      v-model="modalData.trigger_prefix"
                      type="text"
                      id="trigger_prefix"
                      class="form-control"
                      :placeholder="$t('__aiSettingsExamplePrefix')"
                    />
                  </div>
                </div>
              </div>

              <!-- 客服介入設定 -->
              <div class="border rounded p-3 mb-3">
                <h6 class="fw-bold mb-3">{{ $t("__aiSettingsStaffIntervention") }}</h6>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <div class="form-check">
                      <input
                        v-model="modalData.pause_on_staff_reply"
                        type="checkbox"
                        class="form-check-input"
                        id="pause_on_staff_reply"
                      />
                      <label class="form-check-label" for="pause_on_staff_reply">
                        {{ $t("__aiSettingsPauseOnStaffReply") }}
                      </label>
                      <small class="form-text text-muted d-block">
                        {{ $t("__aiSettingsPauseDescription") }}
                      </small>
                    </div>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label for="pause_duration" class="form-label fw-medium">
                      {{ $t("__aiSettingsPauseDuration") }}
                      <small class="text-muted">{{ $t("__aiSettingsPauseDurationUnit") }}</small>
                    </label>
                    <input
                      v-model.number="modalData.pause_duration"
                      type="number"
                      id="pause_duration"
                      class="form-control"
                      min="60"
                      max="3600"
                      step="1"
                      :disabled="!modalData.pause_on_staff_reply"
                    />
                    <small class="form-text text-muted">
                      {{ $t("__aiSettingsPauseDurationHint") }}
                    </small>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <div class="form-check">
                      <input
                        v-model="modalData.staff_trigger_required"
                        type="checkbox"
                        class="form-check-input"
                        id="staff_trigger_required"
                      />
                      <label class="form-check-label" for="staff_trigger_required">
                        {{ $t("__aiSettingsStaffTriggerRequired") }}
                      </label>
                      <small class="form-text text-muted d-block">
                        {{ $t("__aiSettingsStaffTriggerDesc") }}
                      </small>
                    </div>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label fw-medium">{{ $t("__aiSettingsStaffTriggerWords") }}</label>
                    <v-select
                      v-model="modalData.staff_trigger_keywords"
                      :options="['小微', '@AI', '機器人', '助理']"
                      multiple
                      taggable
                      :placeholder="$t('__aiSettingsStaffWordsPlaceholder')"
                      :disabled="!modalData.staff_trigger_required"
                    ></v-select>
                    <small class="form-text text-muted">
                      {{ $t("__aiSettingsStaffWordsHint") }}
                    </small>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-12 mb-3">
                    <div class="form-check">
                      <input
                        v-model="modalData.user_direct_trigger"
                        type="checkbox"
                        class="form-check-input"
                        id="user_direct_trigger"
                      />
                      <label class="form-check-label" for="user_direct_trigger">
                        {{ $t("__aiSettingsUserDirectTrigger") }}
                      </label>
                      <small class="form-text text-muted d-block">
                        {{ $t("__aiSettingsUserDirectTriggerHint") }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 提示詞設定 -->
              <div class="border rounded p-3 mb-3">
                <h6 class="fw-bold mb-3">{{ $t("__aiSettingsPromptConfig") }}</h6>

                <div class="mb-3">
                  <label for="system_prompt" class="form-label fw-medium">{{ $t("__aiSettingsSystemPrompt") }}</label>
                  <textarea
                    v-model="modalData.system_prompt"
                    id="system_prompt"
                    class="form-control"
                    rows="3"
                    :placeholder="$t('__aiSettingsSystemPromptPlaceholder')"
                  ></textarea>
                </div>

                <div class="mb-3">
                  <label for="fallback_message" class="form-label fw-medium">{{ $t("__aiSettingsFallbackMessage") }}</label>
                  <input
                    v-model="modalData.fallback_message"
                    type="text"
                    id="fallback_message"
                    class="form-control"
                    :placeholder="$t('__aiSettingsFallbackPlaceholder')"
                  />
                </div>
              </div>

              <!-- 進階設定 -->
              <div class="border rounded p-3 mb-3">
                <h6 class="fw-bold mb-3">{{ $t("__aiSettingsAdvancedConfig") }}</h6>

                <div class="row">
                  <div class="col-md-4 mb-3">
                    <label for="temperature" class="form-label fw-medium">
                      {{ $t("__aiSettingsTemperature") }}
                      <small class="text-muted">{{ $t("__aiSettingsTemperatureRange") }}</small>
                    </label>
                    <input
                      v-model.number="modalData.temperature"
                      type="number"
                      id="temperature"
                      class="form-control"
                      min="0"
                      max="2"
                      step="0.1"
                    />
                  </div>

                  <div class="col-md-4 mb-3">
                    <label for="max_tokens" class="form-label fw-medium">{{ $t("__aiSettingsMaxTokens") }}</label>
                    <input
                      v-model.number="modalData.max_tokens"
                      type="number"
                      id="max_tokens"
                      class="form-control"
                      min="10"
                      max="4096"
                    />
                  </div>

                  <div class="col-md-4 mb-3">
                    <label for="similarity_threshold" class="form-label fw-medium">
                      {{ $t("__aiSettingsSimilarityThreshold") }}
                      <small class="text-muted">{{ $t("__aiSettingsSimilarityRange") }}</small>
                    </label>
                    <input
                      v-model.number="modalData.similarity_threshold"
                      type="number"
                      id="similarity_threshold"
                      class="form-control"
                      min="0"
                      max="1"
                      step="0.1"
                    />
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-4 mb-3">
                    <label for="max_results" class="form-label fw-medium">{{ $t("__aiSettingsMaxResults") }}</label>
                    <input
                      v-model.number="modalData.max_results"
                      type="number"
                      id="max_results"
                      class="form-control"
                      min="1"
                      max="20"
                    />
                  </div>

                  <div class="col-md-4 mb-3">
                    <label for="cache_timeout" class="form-label fw-medium">{{ $t("__aiSettingsCacheTimeout") }}</label>
                    <input
                      v-model.number="modalData.cache_timeout"
                      type="number"
                      id="cache_timeout"
                      class="form-control"
                      min="0"
                    />
                  </div>

                  <div class="col-md-4 mb-3">
                    <label for="priority" class="form-label fw-medium">
                      {{ $t("__aiSettingsPriority") }}
                      <small class="text-muted">{{ $t("__aiSettingsPriorityHint") }}</small>
                    </label>
                    <input
                      v-model.number="modalData.priority"
                      type="number"
                      id="priority"
                      class="form-control"
                      min="0"
                      max="999"
                      :placeholder="$t('__aiSettingsPriorityPlaceholder')"
                    />
                    <small class="form-text text-muted">
                      {{ $t("__aiSettingsPriorityDesc") }}
                    </small>
                  </div>
                </div>
              </div>

              <!-- 限制設定 -->
              <div class="border rounded p-3 mb-3">
                <h6 class="fw-bold mb-3">{{ $t("__aiSettingsRateLimits") }}</h6>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="rate_limit_per_minute" class="form-label fw-medium">{{ $t("__aiSettingsRateLimitMinute") }}</label>
                    <input
                      v-model.number="modalData.rate_limit_per_minute"
                      type="number"
                      id="rate_limit_per_minute"
                      class="form-control"
                      min="1"
                    />
                  </div>

                  <div class="col-md-6 mb-3">
                    <label for="rate_limit_per_day" class="form-label fw-medium">{{ $t("__aiSettingsRateLimitDay") }}</label>
                    <input
                      v-model.number="modalData.rate_limit_per_day"
                      type="number"
                      id="rate_limit_per_day"
                      class="form-control"
                      min="1"
                    />
                  </div>
                </div>
              </div>

              <!-- 狀態設定 -->
              <div class="border rounded p-3">
                <h6 class="fw-bold mb-3">{{ $t("__status") }}</h6>

                <div class="form-check">
                  <input
                    v-model="modalData.is_active"
                    type="checkbox"
                    class="form-check-input"
                    id="is_active"
                  />
                  <label class="form-check-label" for="is_active">
                    {{ $t("__enabled") }}
                  </label>
                </div>
              </div>
            </div>

            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                {{ $t("__cancel") }}
              </button>
              <button type="submit" class="btn btn-primary">
                {{ isEdit ? $t("__save") : $t("__add") }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- 測試 Modal -->
    <div class="modal fade cms-style" tabindex="-1" id="testModal" ref="testModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <form @submit.prevent="submitTestForm">
            <div class="modal-header">
              <h5 class="modal-title fw-medium">{{ $t("__aiSettingsTestTitle") }}</h5>
              <button
                type="button"
                class="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <label for="testMessage" class="form-label fw-medium required">{{ $t("__aiSettingsTestMessage") }}</label>
                <textarea
                  v-model="testMessage"
                  id="testMessage"
                  class="form-control"
                  rows="3"
                  :placeholder="$t('__aiSettingsTestPlaceholder')"
                  required
                ></textarea>
              </div>

              <div v-if="testResult" class="alert" :class="testResult.success ? 'alert-success' : 'alert-danger'">
                <h6 class="alert-heading">{{ $t("__aiSettingsTestResult") }}</h6>
                <div v-if="testResult.success">
                  <p><strong>{{ $t("__aiSettingsAiReply") }}：</strong></p>
                  <p>{{ testResult.result.response }}</p>
                  <small class="text-muted">
                    {{ $t("__aiSettingsConfidence") }}：{{ (testResult.result.confidence * 100).toFixed(1) }}%
                  </small>
                </div>
                <div v-else>
                  <p>{{ testResult.error }}</p>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                {{ $t("__close") }}
              </button>
              <button type="submit" class="btn btn-primary">
                {{ $t("__aiSettingsTestButton") }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- 詳情 Modal -->
    <div class="modal fade cms-style" tabindex="-1" id="detailsModal" ref="detailsModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title fw-medium">{{ $t("__aiSettingsDetailsTitle") }}</h5>
            <button
              type="button"
              class="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body" v-if="selectedItem">
            <div class="row">
              <div class="col-md-6">
                <h6 class="fw-bold">{{ $t("__aiSettingsBasicInfo") }}</h6>
                <dl>
                  <dt>{{ $t("__agency") }}</dt>
                  <dd>{{ selectedItem.agency ? selectedItem.agency.name : $t('__globalSetting') }}</dd>

                  <dt>{{ $t("__aiSettingsProvider") }}</dt>
                  <dd>{{ selectedItem.provider.toUpperCase() }}</dd>

                  <dt>{{ $t("__aiSettingsModel") }}</dt>
                  <dd>{{ selectedItem.model }}</dd>

                  <dt>{{ $t("__aiSettingsEmbeddingModel") }}</dt>
                  <dd>{{ selectedItem.embedding_model }}</dd>

                  <dt>{{ $t("__status") }}</dt>
                  <dd>
                    <span v-if="selectedItem.is_active" class="badge bg-success">{{ $t("__enabled") }}</span>
                    <span v-else class="badge bg-secondary">{{ $t("__disabled") }}</span>
                  </dd>
                </dl>
              </div>

              <div class="col-md-6">
                <h6 class="fw-bold">{{ $t("__aiSettingsStatisticsInfo") }}</h6>
                <dl>
                  <dt>{{ $t("__aiSettingsTotalRequests") }}</dt>
                  <dd>{{ selectedItem.total_requests || 0 }}</dd>

                  <dt>{{ $t("__aiSettingsSuccessResponses") }}</dt>
                  <dd>{{ selectedItem.successful_responses || 0 }}</dd>

                  <dt>{{ $t("__aiSettingsSuccessRate") }}</dt>
                  <dd v-if="selectedItem.total_requests > 0">
                    {{ ((selectedItem.successful_responses / selectedItem.total_requests) * 100).toFixed(1) }}%
                  </dd>
                  <dd v-else>N/A</dd>

                  <dt>{{ $t("__aiSettingsLastUsedTime") }}</dt>
                  <dd>{{ selectedItem.last_used_at || $t('__aiSettingsNeverUsed') }}</dd>
                </dl>
              </div>
            </div>

            <div class="row mt-3">
              <div class="col-12">
                <h6 class="fw-bold">{{ $t("__aiSettingsTriggerConditionsDetail") }}</h6>
                <ul>
                  <li v-if="selectedItem.trigger_keywords && selectedItem.trigger_keywords.length">
                    {{ $t("__aiSettingsKeywordsList") }}：{{ selectedItem.trigger_keywords.join(', ') }}
                  </li>
                  <li v-if="selectedItem.trigger_on_question">{{ $t("__aiSettingsQuestionTriggerOn") }}</li>
                  <li v-if="selectedItem.trigger_on_mention">{{ $t("__aiSettingsMentionTriggerOn") }}</li>
                  <li v-if="selectedItem.trigger_prefix">{{ $t("__aiSettingsTriggerPrefix") }}：{{ selectedItem.trigger_prefix }}</li>
                </ul>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              {{ $t("__close") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 複製設定 Modal -->
    <div class="modal fade cms-style" tabindex="-1" id="duplicateModal" ref="duplicateModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title fw-medium">
              {{ $t("__aiSettingsDuplicateTitle") }}
            </h5>
            <button
              type="button"
              class="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label for="duplicateTargetAgency" class="form-label fw-medium">
                {{ $t("__aiSettingsDuplicateTarget") }}
              </label>
              <select
                v-model="duplicateTargetAgency"
                class="form-select"
                id="duplicateTargetAgency"
              >
                <option :value="null">{{ $t("__globalSetting") }}</option>
                <option
                  v-for="agency in allAgencys"
                  :key="agency.id"
                  :value="agency.id"
                >
                  {{ agency.name }}
                </option>
              </select>
            </div>
          </div>
          <div class="modal-footer justify-content-between">
            <button
              type="button"
              class="btn btn-outline-secondary rounded-pill"
              data-bs-dismiss="modal"
            >
              {{ $t("__cancel") }}
            </button>
            <button
              type="button"
              class="btn btn-primary rounded-pill"
              @click="confirmDuplicate"
            >
              {{ $t("__aiSettingsDuplicateButton") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Tooltip, Modal } from 'bootstrap'
import i18n from '@/lang/lang.js'
import { aiSettings } from '@/http/api/aiSettings.js'
import { agency } from '@/http/api/agency.js'
import { lineGroup } from '@/http/api/lineGroup.js'
import { mapState, mapMutations } from 'vuex'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
  name: 'AiSettings',
  components: { vSelect },
  data () {
    return {
      sortBy: 'id',
      sortDesc: true,
      allAgencys: [],
      items: [],
      filterItem: null,
      filter: null,
      filterType: null,
      filterOn: ['provider', 'model', 'agency'],
      searchAgency: null,
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      bootstrap_tooltip: null,
      bootstrap_modal: null,
      bootstrap_test_modal: null,
      bootstrap_details_modal: null,
      bootstrap_duplicate_modal: null,
      isEdit: false,
      editModalId: null,
      testSettingId: null,
      testMessage: '',
      testResult: null,
      selectedItem: null,
      duplicateSettingId: null,
      duplicateTargetAgency: null,
      modalData: {
        agency_id: null,
        provider: 'openai',
        api_key: null,
        api_endpoint: null,
        model: 'gpt-4o-mini',
        embedding_model: 'text-embedding-3-small',
        similarity_threshold: 0.7,
        max_results: 5,
        cache_timeout: 3600,
        trigger_keywords: [],
        trigger_on_question: true,
        trigger_on_mention: true,
        trigger_prefix: null,
        staff_trigger_required: true,
        staff_trigger_keywords: ['小微', '@AI', '機器人'],
        user_direct_trigger: true,
        pause_duration: 300,
        pause_on_staff_reply: true,
        system_prompt: '你是一個友善的健康照護助理，專門協助回答長照機構相關問題。請用繁體中文回答，保持專業但親切的語氣。',
        context_prompt: null,
        fallback_message: '抱歉，我現在無法回答您的問題。請稍後再試或聯繫客服人員。',
        prompt_templates: {},
        temperature: 0.7,
        max_tokens: 500,
        enable_learning: true,
        enable_feedback: true,
        auto_translate: false,
        default_language: 'zh-TW',
        rate_limit_per_minute: 10,
        rate_limit_per_day: 1000,
        max_conversation_length: 10,
        is_active: true,
        priority: 0
      },
      validate: null,
      availableModels: {
        main: [],
        embedding: []
      },
      modelsNote: '',
      embeddingNote: '',
      modelLoadTimer: null,
      defaultTriggerKeywords: [
        '@AI', '@ai', '小助手', '助理', '請問', '詢問',
        '想知道', '可以告訴我', '幫我', '教我',
        '如何', '怎麼', '什麼是', '為什麼'
      ],
      // 測試模式相關
      globalTestSetting: null,
      testMode: false,
      testGroupIds: [],
      availableGroups: []
    }
  },
  computed: {
    ...mapState(['token', 'userInfo']),
    permissions () {
      let permissions = null
      if (this.userInfo) {
        permissions = this.userInfo.permissions
      }
      return permissions
    },
    fields () {
      return [
        {
          key: 'id',
          label: 'ID',
          class: 'align-middle',
          sortable: true
        },
        {
          key: 'agency',
          label: this.$t('__agency'),
          class: 'align-middle',
          sortable: false
        },
        {
          key: 'provider',
          label: this.$t('__aiSettingsProvider'),
          class: 'align-middle',
          sortable: true
        },
        {
          key: 'model',
          label: this.$t('__aiSettingsModel'),
          class: 'align-middle',
          sortable: false
        },
        {
          key: 'priority',
          label: this.$t('__aiSettingsPriority'),
          class: 'align-middle text-center',
          sortable: true
        },
        {
          key: 'is_active',
          label: this.$t('__status'),
          class: 'align-middle text-center',
          sortable: true
        },
        {
          key: 'statistics',
          label: this.$t('__aiSettingsStatistics'),
          class: 'align-middle',
          sortable: false
        },
        {
          key: 'actions',
          label: this.$t('__operation'),
          class: 'align-middle text-center'
        }
      ]
    }
  },
  watch: {
    filterType () {
      this.filterItems()
    },
    searchAgency () {
      this.filterItems()
    },
    modalData: {
      handler: function () {
        this.validate = null
      },
      deep: true
    },
    'modalData.provider': {
      handler: function (newProvider) {
        this.loadAvailableModels(newProvider, this.modalData.api_key)
      }
    },
    'modalData.api_key': {
      handler: function (newKey) {
        if (this.modalData.provider === 'openai' && newKey) {
          // 延遲載入，避免輸入時頻繁請求
          clearTimeout(this.modelLoadTimer)
          this.modelLoadTimer = setTimeout(() => {
            this.loadAvailableModels(this.modalData.provider, newKey)
          }, 1000)
        }
      }
    }
  },
  methods: {
    ...mapMutations(['Loading', 'Loaded', 'AlertsInfo', 'Alerted']),

    getAllAgency () {
      const vm = this
      agency.get('', this.token).then((res) => {
        if (res.status <= 201 && res.data.status === 'success') {
          vm.allAgencys = res.data.data
        }
      })
    },

    getAiSettings () {
      const vm = this
      vm.Loading()
      aiSettings
        .get('', vm.token)
        .then((res) => {
          if (res.status <= 201) {
            vm.items = res.data.data || res.data
            vm.filterItems()

            // 找出全域測試設定
            vm.globalTestSetting = vm.items.find(item =>
              !item.agency_id && item.is_active
            )

            if (vm.globalTestSetting) {
              vm.testMode = vm.globalTestSetting.test_mode || false
              vm.testGroupIds = vm.globalTestSetting.test_group_ids || []
            }
          } else {
            vm.AlertsInfo({
              state: 'error',
              title: i18n.t('__error'),
              info: res.data.message || i18n.t('__loadSettingsFailed')
            })
            vm.Alerted()
          }
          vm.Loaded()
        })
        .catch((err) => {
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: err.message || err
          })
          vm.Alerted()
          vm.Loaded()
        })
    },

    filterItems () {
      let filtered = this.items

      if (this.filterType === 'global') {
        filtered = filtered.filter(item => !item.agency_id)
      } else if (this.filterType === 'agency') {
        filtered = filtered.filter(item => item.agency_id)
      }

      if (this.searchAgency) {
        filtered = filtered.filter(item => item.agency_id === this.searchAgency)
      }

      this.filterItem = filtered
      this.totalRows = filtered.length
    },

    onFiltered (filteredItems) {
      this.totalRows = filteredItems.length
      this.currentPage = 1
    },

    initBootstrapTooltip () {
      this.$nextTick(function () {
        this.bootstrap_tooltip = [].slice
          .call(document.querySelectorAll('[data-bs-toggle="tooltip"]'))
          .map(function (tooltipTriggerEl) {
            return new Tooltip(tooltipTriggerEl)
          })
        document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(ele => {
          ele.addEventListener('shown.bs.tooltip', function () {
            setTimeout(() => {
              Tooltip.getInstance(ele).hide()
            }, 600)
          })
        })
      })
    },

    showModal (id = null) {
      this.isEdit = !!id
      this.editModalId = id

      if (id) {
        const item = this.items.find(i => i.id === id)
        if (item) {
          console.log('編輯項目資料:', item)
          console.log('API Key:', item.api_key)
          this.modalData = { ...item }
          // 記錄是否有 API Key（用於顯示提示）
          this.modalData.has_api_key = !!item.api_key
          // 顯示 API Key（開發測試用，正式環境應註解掉）
          // this.modalData.api_key = null
          console.log('modalData.api_key:', this.modalData.api_key)
        }
      } else {
        // 重置為預設值
        this.modalData = {
          agency_id: null,
          provider: 'openai',
          api_key: null,
          api_endpoint: null,
          model: 'gpt-4o-mini',
          embedding_model: 'text-embedding-3-small',
          similarity_threshold: 0.7,
          max_results: 5,
          cache_timeout: 3600,
          trigger_keywords: ['@AI', '請問', '如何', '怎麼'],
          trigger_on_question: true,
          trigger_on_mention: true,
          trigger_prefix: null,
          staff_trigger_required: true,
          staff_trigger_keywords: ['小微', '@AI', '機器人'],
          user_direct_trigger: true,
          pause_duration: 300,
          pause_on_staff_reply: true,
          system_prompt: '你是一個友善的健康照護助理，專門協助回答長照機構相關問題。請用繁體中文回答，保持專業但親切的語氣。',
          context_prompt: null,
          fallback_message: '抱歉，我現在無法回答您的問題。請稍後再試或聯繫客服人員。',
          prompt_templates: {},
          temperature: 0.7,
          max_tokens: 500,
          enable_learning: true,
          enable_feedback: true,
          auto_translate: false,
          default_language: 'zh-TW',
          rate_limit_per_minute: 10,
          rate_limit_per_day: 1000,
          max_conversation_length: 10,
          is_active: true,
          priority: 0
        }
      }

      this.validate = null

      // 載入可用模型清單
      this.loadAvailableModels(this.modalData.provider, this.modalData.api_key)

      this.bootstrap_modal = new Modal(this.$refs.editModal)
      this.bootstrap_modal.show()
    },

    async loadAvailableModels(provider, apiKey) {
      try {
        const token = localStorage.getItem('humetrics_token')
        const params = {
          provider: provider || 'openai',
          api_key: apiKey || ''
        }

        const response = await axiosConfig.get('/api/ai/settings/available-models', {
          params,
          headers: { Authorization: `Bearer ${token}` }
        })

        if (response.status === 200) {
          this.availableModels = response.data

          // 設定提示訊息
          if (response.data.note) {
            this.modelsNote = response.data.note
            this.embeddingNote = response.data.note
          } else if (response.data.error) {
            this.modelsNote = response.data.error
            this.embeddingNote = response.data.error
          } else {
            this.modelsNote = provider === 'openai' && apiKey
              ? '從 OpenAI API 取得的即時模型清單'
              : '請提供 API Key 以取得即時模型清單'
            this.embeddingNote = provider === 'claude'
              ? 'Claude 不提供 embedding 模型'
              : this.modelsNote
          }
        }
      } catch (error) {
        console.error('載入模型清單失敗:', error)
        // 設定預設模型清單
        if (provider === 'openai') {
          this.availableModels = {
            main: ['gpt-4-turbo-preview', 'gpt-4', 'gpt-3.5-turbo'],
            embedding: ['text-embedding-3-small', 'text-embedding-3-large']
          }
          this.modelsNote = i18n.t('__aiSettingsModelListLoadFailed')
        }
      }
    },

    submitForm () {
      const vm = this
      vm.Loading()

      const data = { ...this.modalData }

      // 如果是編輯且沒有輸入新 API Key，移除該欄位
      if (this.isEdit && !data.api_key) {
        delete data.api_key
      }

      const promise = this.isEdit
        ? aiSettings.update(this.editModalId, data, this.token)
        : aiSettings.create(data, this.token)

      promise
        .then((res) => {
          if (res.status <= 201) {
            vm.AlertsInfo({
              state: 'success',
              title: i18n.t('__success'),
              info: this.isEdit ? i18n.t('__aiUpdateSuccess') : i18n.t('__aiCreateSuccess')
            })
            vm.Alerted()
            vm.bootstrap_modal.hide()
            vm.getAiSettings()
          } else {
            vm.AlertsInfo({
              state: 'error',
              title: i18n.t('__error'),
              info: res.data.message || i18n.t('__aiOperationFailed')
            })
            vm.Alerted()
          }
          vm.Loaded()
        })
        .catch((err) => {
          if (err.response && err.response.status === 422) {
            vm.validate = err.response.data.errors
          } else {
            vm.AlertsInfo({
              state: 'error',
              title: i18n.t('__error'),
              info: err.message || err
            })
            vm.Alerted()
          }
          vm.Loaded()
        })
    },

    deleteSettingAlert (id) {
      if (confirm(this.$t('__aiSettingsConfirmDelete'))) {
        this.deleteSetting(id)
      }
    },

    deleteSetting (id) {
      const vm = this
      vm.Loading()
      aiSettings
        .delete(id, this.token)
        .then((res) => {
          if (res.status <= 201) {
            vm.AlertsInfo({
              state: 'success',
              title: i18n.t('__success'),
              info: i18n.t('__deleteSuccess')
            })
            vm.Alerted()
            vm.getAiSettings()
          } else {
            vm.AlertsInfo({
              state: 'error',
              title: i18n.t('__error'),
              info: res.data.message || i18n.t('__aiDeleteFailed')
            })
            vm.Alerted()
          }
          vm.Loaded()
        })
        .catch((err) => {
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: err.message || err
          })
          vm.Alerted()
          vm.Loaded()
        })
    },

    testSetting (id) {
      this.testSettingId = id
      this.testMessage = ''
      this.testResult = null
      this.bootstrap_test_modal = new Modal(this.$refs.testModal)
      this.bootstrap_test_modal.show()
    },

    submitTestForm () {
      const vm = this
      vm.Loading()
      aiSettings
        .test(this.testSettingId, { message: this.testMessage }, this.token)
        .then((res) => {
          if (res.status <= 201) {
            vm.testResult = res.data
          } else {
            vm.testResult = {
              success: false,
              error: res.data.message || '測試失敗'
            }
          }
          vm.Loaded()
        })
        .catch((err) => {
          vm.testResult = {
            success: false,
            error: err.message || '測試失敗'
          }
          vm.Loaded()
        })
    },

    getLineGroups () {
      const vm = this
      lineGroup
        .get('', vm.token)
        .then((res) => {
          if (res.status <= 201) {
            const data = res.data.data || res.data
            vm.availableGroups = data.map(group => ({
              ...group,
              display_name: group.display_name ||
                           group.group_name ||
                           (group.agency ? group.agency.name : '') ||
                           group.bind_id ||
                           group.group_id.substring(0, 10) + '...'
            }))
          }
        })
        .catch((error) => {
          console.error('Error fetching LINE groups:', error)
        })
    },

    updateTestMode () {
      if (!this.globalTestSetting) return

      const vm = this
      this.Loading()

      const updateData = {
        test_mode: this.testMode,
        test_group_ids: this.testMode ? this.testGroupIds : []
      }

      aiSettings.update(this.globalTestSetting.id, updateData, this.token)
        .then(res => {
          if (res.status <= 201) {
            vm.AlertsInfo({
              state: 'success',
              title: '更新成功',
              info: vm.testMode ? '測試模式已開啟' : '測試模式已關閉'
            })
            vm.Alerted()
            vm.getAiSettings()
          }
        })
        .catch(error => {
          console.error('Error updating test mode:', error)
          vm.AlertsInfo({
            state: 'error',
            title: '更新失敗',
            info: error.response?.data?.message || '無法更新測試模式'
          })
          vm.Alerted()
        })
        .finally(() => {
          vm.Loaded()
        })
    },

    updateTestGroups () {
      if (!this.globalTestSetting || !this.testMode) return

      const vm = this
      this.Loading()

      const updateData = {
        test_group_ids: this.testGroupIds
      }

      aiSettings.update(this.globalTestSetting.id, updateData, this.token)
        .then(res => {
          if (res.status <= 201) {
            vm.AlertsInfo({
              state: 'success',
              title: '更新成功',
              info: `已選擇 ${vm.testGroupIds.length} 個測試群組`
            })
            vm.Alerted()
          }
        })
        .catch(error => {
          console.error('Error updating test groups:', error)
          vm.AlertsInfo({
            state: 'error',
            title: '更新失敗',
            info: error.response?.data?.message || '無法更新測試群組'
          })
          vm.Alerted()
        })
        .finally(() => {
          vm.Loaded()
        })
    },

    duplicateSetting (id) {
      const vm = this
      vm.duplicateSettingId = id
      vm.duplicateTargetAgency = null
      vm.bootstrap_duplicate_modal = new Modal(vm.$refs.duplicateModal)
      vm.$nextTick(function () {
        vm.bootstrap_duplicate_modal.show()
      })
    },

    confirmDuplicate () {
      const vm = this
      vm.Loading()
      aiSettings
        .duplicate(vm.duplicateSettingId, { agency_id: vm.duplicateTargetAgency || null }, this.token)
        .then((res) => {
          if (res.status <= 201) {
            vm.AlertsInfo({
              state: 'success',
              title: i18n.t('__success'),
              info: i18n.t('__aiSettingsDuplicateSuccess')
            })
            vm.Alerted()
            vm.getAiSettings()
            vm.bootstrap_duplicate_modal.hide()
          } else {
            vm.AlertsInfo({
              state: 'error',
              title: i18n.t('__error'),
              info: res.data.message || i18n.t('__aiSettingsDuplicateFailed')
            })
            vm.Alerted()
          }
          vm.Loaded()
        })
        .catch((err) => {
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: err.message || err
          })
          vm.Alerted()
          vm.Loaded()
        })
    },

    showDetails (item) {
      this.selectedItem = item
      this.bootstrap_details_modal = new Modal(this.$refs.detailsModal)
      this.bootstrap_details_modal.show()
    },

    validateApiKey () {
      const vm = this
      vm.Loading()
      aiSettings
        .validateApiKey({
          provider: this.modalData.provider,
          api_key: this.modalData.api_key
        }, this.token)
        .then((res) => {
          if (res.status <= 201 && res.data.valid) {
            vm.AlertsInfo({
              state: 'success',
              title: i18n.t('__aiSettingsValidateSuccess'),
              info: res.data.message || i18n.t('__aiSettingsApiKeyValid')
            })
            // 驗證成功後重新載入模型清單
            vm.loadAvailableModels(vm.modalData.provider, vm.modalData.api_key)
          } else {
            vm.AlertsInfo({
              state: 'error',
              title: i18n.t('__aiSettingsValidateFailed'),
              info: res.data.message || i18n.t('__aiSettingsApiKeyInvalid')
            })
          }
          vm.Alerted()
          vm.Loaded()
        })
        .catch((err) => {
          vm.AlertsInfo({
            state: 'error',
            title: i18n.t('__error'),
            info: err.message || i18n.t('__aiSettingsValidationFailed')
          })
          vm.Alerted()
          vm.Loaded()
        })
    }
  },
  mounted () {
    this.getAllAgency()
    this.getAiSettings()
    this.getLineGroups()
    this.initBootstrapTooltip()
  },
  updated () {
    this.initBootstrapTooltip()
  }
}
</script>

<style scoped>
.required:after {
  content: " *";
  color: #dc3545;
}

.status-switch {
  margin: 0 10px !important;
}
</style>
